import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { formatDate } from "@/lib/format";
import { FolderKanban } from "lucide-react";
import type { Project } from "@db/schema";

type PortalProject = Project & {
  owner: { firstName: string | null; lastName: string | null; email: string } | null;
};

const columns: ColumnDef<PortalProject>[] = [
  {
    key: "name",
    header: "Project",
    cell: (row) => <span className="font-medium">{row.name}</span>,
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => row.status.replace(/_/g, " "),
  },
  {
    key: "owner",
    header: "Owner",
    cell: (row) =>
      row.owner
        ? `${row.owner.firstName || ""} ${row.owner.lastName || ""}`.trim() ||
          row.owner.email
        : "Unassigned",
  },
  {
    key: "targetEndDate",
    header: "Target End",
    cell: (row) => (row.targetEndDate ? formatDate(row.targetEndDate) : "TBD"),
  },
];

export default function ClientProjects() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<PortalProject[]>({
    queryKey: ["/api/portal/projects"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filtered = data?.filter((project) => {
    if (!search) return true;
    return project.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Track the delivery work your team has chosen to share."
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search projects..."
        emptyIcon={FolderKanban}
        emptyTitle="No visible projects"
        emptyDescription="Projects marked as client-visible will appear here."
      />
    </div>
  );
}
